

import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpService } from './utility/http-service';
import { ModalType } from './utility/dialog-template/dialog-template.component';
import { ComboBoxComponent, ComboBoxModule } from '@syncfusion/ej2-angular-dropdowns'
import { RouterService } from './utility/router-service';
import { environment } from '../environments/environment';
import { LoginService, UserClient, UserContext } from '../services/login-service';
import { RecentlyViewedService } from './services/recently-viewed.service';
import { IdleTimeoutService } from '../services/idle-timeout.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'Well Chron';
  isIframe = false;
  private readonly _destroying$ = new Subject<void>();
  sidebarOpen: boolean = false;

  // A getter rather than a value set from a NavigationEnd subscription: the
  // fresh-login redirect chain (callback -> CheckHomeGuard -> final route)
  // fires several navigations in quick succession interleaved with async guard
  // work, and a subscription set up in the constructor could miss the one that
  // lands on the final route, leaving this stuck at its default. Reading the
  // router's own current url on every change-detection cycle instead means
  // there's no state to fall out of sync in the first place.
  get showHeader(): boolean {
    const url = this.router.router.url;
    return !url.startsWith('/aquatrade') && !url.startsWith('/watersharing');
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }
  @ViewChild(ComboBoxComponent)
  comboBox?: ComboBoxComponent;
  @ViewChild('sidebarEl') sidebarEl!: ElementRef;
  @ViewChild('toggleBtn') toggleBtn!: ElementRef;
  @ViewChild('topbarEl') topbarEl?: ElementRef<HTMLDivElement>;
  private topbarResizeObserver?: ResizeObserver;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.sidebarOpen) return;
    const target = event.target as Node;
    const insideSidebar = this.sidebarEl?.nativeElement.contains(target);
    const insideToggle = this.toggleBtn?.nativeElement.contains(target);
    // Syncfusion popups (e.g. the client combobox list) are appended to document.body,
    // so they'd otherwise count as an outside click and close the sidebar.
    const element = target instanceof Element ? target : target?.parentElement ?? null;
    const insidePopup = !!element?.closest('.e-popup');
    if (!insideSidebar && !insideToggle && !insidePopup) {
      this.closeSidebar();
    }
  }
  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private http: HttpService,
    private router: RouterService,
    public loginService: LoginService,
    private idleTimeoutService: IdleTimeoutService,
    _recentlyViewed: RecentlyViewedService  // eagerly instantiate so router-event listener starts immediately
  )
  {
  }

  async ngOnInit() : Promise<void> {
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    // The SDK processes the ?code=&state= callback itself and flips
    // isAuthenticated$ once a session exists -- there is no redirect promise to
    // await here, and APP_INITIALIZER (initApp in app-config.ts) has already
    // waited for isLoading$ before the app rendered.
    // Surface SDK failures. Without this a failed code-for-token exchange is
    // silent: isLoading$ completes, isAuthenticated$ stays false, and the app
    // sits on the callback URL with ?code= still in the address bar giving no
    // indication of why.
    this.authService.error$
      .pipe(takeUntil(this._destroying$))
      .subscribe((err) => {
        console.error('[auth0] ', err);
        if (AppComponent.SESSION_EXPIRED_ERRORS.includes((err as any)?.error)) {
          this.reauthenticate();
        }
      });

    this.authService.isAuthenticated$
      .pipe(takeUntil(this._destroying$))
      .subscribe(async (isAuthenticated) => {
        if (isAuthenticated) {
          try {
            await this.loginService.ensureUserLoaded();
            this.idleTimeoutService.start(() => this.logout());
          } catch {
            // Left unhandled this rejects silently on every emission of this
            // observable (repeated failed requests, each logged to the console
            // on its own). UserHydrationGuard is what actually routes an
            // unhydrated user to /login-failed -- this subscription only needs
            // to not blow up, and must not start the idle timeout against a
            // session that never successfully loaded a user.
          }
        } else {
          this.idleTimeoutService.stop();
        }
      });
  }

  get displayName(): string | null {
    const u = this.loginService.userContext;
    if (!u) return null;
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return name || null;
  }

  async exitImpersonation() : Promise<void> {
    this.http.post("User/ExitImpersonation", async () => {
      await this.loginService.reload();
      this.router.router.navigateByUrl('/');
    })
  }

  /**
   * Not referenced by the template today -- AuthGuard on the routes triggers the
   * redirect for anonymous visitors. Kept as the single explicit entry point for
   * a "Sign in" control if one is ever added.
   */
  login() {
    this.authService.loginWithRedirect();
  }

  /**
   * SDK error codes meaning the cached session can no longer be renewed
   * silently -- the access token expired and there is no usable refresh token
   * (missing, revoked, or rotated out). Nothing short of an interactive login
   * recovers from these; left alone, every API call just fails the same way.
   */
  private static readonly SESSION_EXPIRED_ERRORS = [
    'missing_refresh_token',
    'invalid_grant',
    'login_required',
    'consent_required',
    'interaction_required',
    'missing_token',
  ];
  private static readonly REAUTH_GUARD_KEY = 'wc.reauthAt';
  private static readonly REAUTH_GUARD_MS = 30_000;
  private reauthenticating = false;

  /**
   * Send the user back through Auth0 login, returning them to where they were.
   *
   * Every in-flight API call fails with the same error, so this fires several
   * times in a burst -- `reauthenticating` collapses that to one redirect. The
   * sessionStorage timestamp stops a redirect loop if the error recurs straight
   * after a fresh login (e.g. a tenant misconfiguration): the second time round
   * the user lands on /login-failed instead of bouncing forever.
   */
  private reauthenticate() {
    if (this.reauthenticating) return;
    const url = this.router.router.url;
    if (url.startsWith('/callback') || url.startsWith('/login-failed')) return;

    let lastAttempt = 0;
    try { lastAttempt = Number(sessionStorage.getItem(AppComponent.REAUTH_GUARD_KEY)) || 0; } catch {}
    if (Date.now() - lastAttempt < AppComponent.REAUTH_GUARD_MS) {
      this.router.router.navigateByUrl('/login-failed');
      return;
    }

    this.reauthenticating = true;
    this.idleTimeoutService.stop();
    try { sessionStorage.setItem(AppComponent.REAUTH_GUARD_KEY, String(Date.now())); } catch {}
    this.authService.loginWithRedirect({ appState: { target: url } });
  }

  logout() {
    this.idleTimeoutService.stop();
    const endAuth0Session = () => {
      this.authService.logout({
        logoutParams: { returnTo: window.location.origin },
      });
    };
    // Revoke the server-side token before ending the Auth0 session, so the token
    // can't still be used against the API after the user has logged out.
    this.http.postReturn('User/Logout', endAuth0Session, undefined, undefined, endAuth0Session);
  }

  /**
   * Auth0 has no equivalent of the B2C "change password" policy redirect: a
   * database-connection user resets via an emailed link. This posts to the
   * public /dbconnections/change_password endpoint, which always returns 200
   * regardless of whether the address exists, so nothing here reveals account
   * existence.
   */
  async changePassword(): Promise<void> {
    const profile = await firstValueFrom(this.authService.user$);
    const email = profile?.email;

    if (!email) {
      this.http.dialogService.showError(
        'Could not determine your email address. Please sign out and back in, then try again.'
      );
      return;
    }

    try {
      await firstValueFrom(
        this.httpClient.post(
          `https://${environment.auth0.domain}/dbconnections/change_password`,
          {
            client_id: environment.auth0.clientId,
            email,
            connection: 'Username-Password-Authentication',
          },
          { responseType: 'text' }
        )
      );

      this.http.dialogService.openModal(
        'Check your email',
        `We've sent a password reset link to ${email}.`,
        ModalType.INFO
      );
    } catch {
      this.http.dialogService.showError(
        'Unable to start a password reset, please contact WellChron support'
      );
    }
  }

  ngAfterViewInit(): void {
    if (!this.topbarEl) return;
    const el = this.topbarEl.nativeElement;
    const updateTopbarHeight = () => {
      document.documentElement.style.setProperty('--wc-topbar-h', `${el.offsetHeight}px`);
    };
    this.topbarResizeObserver = new ResizeObserver(updateTopbarHeight);
    this.topbarResizeObserver.observe(el);
    updateTopbarHeight();
  }

  ngOnDestroy(): void {
    this.topbarResizeObserver?.disconnect();
    this.idleTimeoutService.stop();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  private sortedClientsSource?: UserClient[];
  private sortedClientsCache: UserClient[] = [];

  /** Available clients sorted by display name. Cached so the combobox isn't re-bound on every change detection cycle. */
  get sortedClients(): UserClient[] {
    const clients = this.loginService.userOptional?.availableClients;
    if (!clients) return [];
    if (clients !== this.sortedClientsSource) {
      this.sortedClientsSource = clients;
      this.sortedClientsCache = [...clients].sort((a, b) =>
        (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' }));
    }
    return this.sortedClientsCache;
  }

  onSelectClient(e: any) {
    var value = e.itemData.id;
    this.http.post("User/SwitchClient", () => {
      this.router.reloadPage();
    }, { id: value })
  }
}

